import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/ui/PageHeader'
import {
  AutomationRun,
  AutomationRunStep,
  AutomationSequence,
  getRun,
  getSequence,
  listRuns,
  replayRun,
} from '@/api/labAutomation'

// Feature #262: run history + replay. Two routes mount this:
//
//  /automation/sequences/:id/runs  — list runs for one sequence.
//  /automation/runs/:id            — drill into one run's per-step
//                                    timeline, with a replay button
//                                    on terminal-failed runs.

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
}

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
}

const primaryBtn: React.CSSProperties = {
  padding: '6px 14px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

const statusColor = (status: string): string => {
  switch (status) {
    case 'Succeeded':
      return '#16a34a'
    case 'Running':
      return '#d97706'
    case 'Failed':
    case 'Halted':
      return '#dc2626'
    default:
      return 'var(--color-dark-grey)'
  }
}

const formatDateTime = (s: string | null | undefined): string => {
  if (!s) return '—'
  try {
    return new Date(s).toLocaleString()
  } catch {
    return s
  }
}

// ── Run list page ──────────────────────────────────────────────

export const AutomationRunList: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [runs, setRuns] = useState<AutomationRun[]>([])
  const [sequence, setSequence] = useState<AutomationSequence | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    Promise.all([getSequence(id), listRuns(id)])
      .then(([seq, runs]) => {
        setSequence(seq)
        setRuns(runs)
      })
      .finally(() => setLoading(false))
  }, [id])

  if (!id) return null

  return (
    <div>
      <PageHeader
        title={sequence ? `Runs: ${sequence.name}` : 'Run history'}
        subtitle={
          sequence
            ? `${sequence.sourceModule}/${sequence.triggerEventType} · ${sequence.steps.length} step(s)`
            : ''
        }
      />
      <div style={{ marginTop: '16px', marginBottom: '12px' }}>
        <button onClick={() => navigate('/automation/sequences')} style={secondaryBtn}>
          ← Back to sequences
        </button>
      </div>
      {loading && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}
      {!loading && runs.length === 0 && (
        <div style={cardStyle}>
          <div style={{ color: 'var(--color-dark-grey)' }}>
            No runs yet. Test-fire from the sequence editor to record the first run.
          </div>
        </div>
      )}
      {!loading && runs.length > 0 && (
        <div style={cardStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-dark-grey)' }}>
                <th style={{ padding: '8px', borderBottom: '1px solid var(--color-light-grey)' }}>Triggered</th>
                <th style={{ padding: '8px', borderBottom: '1px solid var(--color-light-grey)' }}>Status</th>
                <th style={{ padding: '8px', borderBottom: '1px solid var(--color-light-grey)' }}>Entity</th>
                <th style={{ padding: '8px', borderBottom: '1px solid var(--color-light-grey)' }}>Steps</th>
                <th style={{ padding: '8px', borderBottom: '1px solid var(--color-light-grey)' }}></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td style={{ padding: '8px' }}>{formatDateTime(r.triggeredAt)}</td>
                  <td style={{ padding: '8px' }}>
                    <span style={{ color: statusColor(r.status), fontWeight: 600 }}>{r.status}</span>
                    {r.replayOfRunId && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--color-dark-grey)' }}>
                        (replay)
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>
                    {r.sourceEntityType}/{r.sourceEntityId.slice(0, 8)}…
                  </td>
                  <td style={{ padding: '8px' }}>{r.steps.length}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    <button
                      onClick={() => navigate(`/automation/runs/${r.id}`)}
                      style={secondaryBtn}
                    >
                      Details →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Run detail page ──────────────────────────────────────────────

export const AutomationRunDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [run, setRun] = useState<AutomationRun | null>(null)
  const [loading, setLoading] = useState(true)
  const [replaying, setReplaying] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    getRun(id)
      .then(setRun)
      .finally(() => setLoading(false))
  }, [id])

  const replay = async () => {
    if (!run) return
    setReplaying(true)
    setErrorMsg(null)
    try {
      const result = await replayRun(run.id)
      navigate(`/automation/runs/${result.run_id}`)
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErrorMsg(detail || (err as Error).message || 'Replay failed')
    } finally {
      setReplaying(false)
    }
  }

  if (!id) return null

  return (
    <div>
      <PageHeader
        title={`Run ${id.slice(0, 8)}…`}
        subtitle={run ? `${run.sourceModule} / ${run.sourceEntityType}/${run.sourceEntityId}` : ''}
      />
      <div style={{ marginTop: '16px', marginBottom: '12px' }}>
        {run && (
          <button onClick={() => navigate(`/automation/sequences/${run.sequenceId}/runs`)} style={secondaryBtn}>
            ← Back to runs
          </button>
        )}
      </div>
      {errorMsg && (
        <div
          style={{
            marginBottom: '12px',
            padding: '8px 12px',
            backgroundColor: 'rgba(220, 53, 69, 0.1)',
            color: '#dc3545',
            borderRadius: '6px',
            fontSize: '13px',
          }}
        >
          {errorMsg}
        </div>
      )}
      {loading && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}
      {!loading && run && (
        <>
          <div style={{ ...cardStyle, marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <div style={labelStyle}>Status</div>
                <div style={{ color: statusColor(run.status), fontWeight: 600, marginTop: '4px' }}>
                  {run.status}
                </div>
              </div>
              <div>
                <div style={labelStyle}>Triggered</div>
                <div style={{ marginTop: '4px', fontSize: '13px' }}>{formatDateTime(run.triggeredAt)}</div>
              </div>
              <div>
                <div style={labelStyle}>Completed</div>
                <div style={{ marginTop: '4px', fontSize: '13px' }}>{formatDateTime(run.completedAt)}</div>
              </div>
              <div>
                <div style={labelStyle}>Trigger event</div>
                <div style={{ marginTop: '4px', fontSize: '11px', fontFamily: 'monospace' }}>
                  {run.triggerEventId || '—'}
                </div>
              </div>
            </div>
            {run.failureReason && (
              <div style={{ marginTop: '16px' }}>
                <div style={labelStyle}>Failure reason</div>
                <pre style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626', whiteSpace: 'pre-wrap' }}>
                  {run.failureReason}
                </pre>
              </div>
            )}
            {run.replayOfRunId && (
              <div style={{ marginTop: '12px' }}>
                <div style={labelStyle}>Replay of</div>
                <button
                  onClick={() => navigate(`/automation/runs/${run.replayOfRunId}`)}
                  style={{ ...secondaryBtn, marginTop: '4px' }}
                >
                  {run.replayOfRunId}
                </button>
              </div>
            )}
            {(run.status === 'Failed' || run.status === 'Halted') && (
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={replay} disabled={replaying} style={primaryBtn}>
                  {replaying ? 'Replaying…' : 'Replay this run'}
                </button>
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>
              Steps ({run.steps.length})
            </h3>
            {run.steps.map((s: AutomationRunStep) => (
              <div
                key={s.id}
                style={{
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '8px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', marginRight: '8px' }}>
                      #{s.stepOrder}
                    </span>
                    <span style={{ color: statusColor(s.status), fontWeight: 600, fontSize: '13px' }}>
                      {s.status}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--color-dark-grey)' }}>
                    {formatDateTime(s.startedAt)} → {formatDateTime(s.completedAt)} (attempts: {s.attemptCount})
                  </div>
                </div>
                {s.requestSummary && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={labelStyle}>Request</div>
                    <pre
                      style={{
                        margin: '4px 0 0 0',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        color: 'var(--color-dark-grey)',
                      }}
                    >
                      {s.requestSummary}
                    </pre>
                  </div>
                )}
                {s.responseSummary && (
                  <div style={{ marginTop: '6px' }}>
                    <div style={labelStyle}>Response</div>
                    <pre
                      style={{
                        margin: '4px 0 0 0',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {s.responseSummary}
                    </pre>
                  </div>
                )}
                {s.capturedValues && Object.keys(s.capturedValues).length > 0 && (
                  <div style={{ marginTop: '6px' }}>
                    <div style={labelStyle}>Captured</div>
                    <pre
                      style={{
                        margin: '4px 0 0 0',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {JSON.stringify(s.capturedValues, null, 2)}
                    </pre>
                  </div>
                )}
                {s.errorMessage && (
                  <div style={{ marginTop: '6px' }}>
                    <div style={labelStyle}>Error</div>
                    <pre
                      style={{
                        margin: '4px 0 0 0',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        color: '#dc2626',
                      }}
                    >
                      {s.errorMessage}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
